import { fetchUser } from "actions/userActions";
import { ErrorBoundary } from "components/Common/ErrorBoundary";
import { GlobalSnackbar } from "components/Common/GlobalSnackbar";
import { EntitlementSystem } from "components/Entitlement";
import { PageLogger } from "components/PageLogger";
import { SetupDialogs } from "components/SetupDialogs";
import { DemoSetupDialog } from "components/SetupDialogs/DemoSetupDialog";
import { TenantNotAllowedDialog } from "components/SetupDialogs/TenantNotAllowedDialog";
import { UserbackProvider } from "components/UserbackProvider";
import { selectRoles, setRoles } from "features/roles";
import { usersSelectors } from "features/users";
import {
	useActionOnScreenTreshold,
	useAppSelector,
	useAuth,
	useEntitlement,
	useEnvironment,
	useTenantIsAllowed,
} from "hooks/hooks";
import { useHotjar } from "hooks/useHotjar";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import breakpoints from "styles/utilities/_breakpoints.scss";
import { NAVIGATION_NAMES } from "utilities/constants/pages";
import { calculateRoles } from "utilities/roleUtils/calculateRoles";
import { LeftNavigation } from "../LeftNavigation";
import { UserDrawer } from "../MyPeople/UserDrawer";
import styles from "./AuthenticatedContent.module.scss";
import { Baguette } from "./Baguette";
import { EnvironmentBanner } from "./EnvironmentBanner/EnvironmentBanner";
import { RoutedContent } from "./RoutedContent";

const AuthenticatedContent = () => {
	useHotjar();
	const {
		dispatch,
		auth,
		auth: {
			instance,
			account,
			account: { localAccountId, username, name, idTokenClaims, tenantId } = {},
			authConfig,
		},
	} = useAuth();
	const { isHomeTenant } = useEntitlement();

	useEffect(() => {
		const setRolesForUser = async () => {
			const calculatedRoles = await calculateRoles({
				authConfig,
				account,
				isHomeTenant,
				instance,
			});
			dispatch(setRoles(calculatedRoles));
		};
		dispatch(fetchUser({ auth, id: localAccountId }));
		setRolesForUser();
	}, [dispatch, auth, localAccountId, account, authConfig, isHomeTenant, instance]);

	const userData = useAppSelector((state) => usersSelectors.selectById(state, localAccountId));

	useActionOnScreenTreshold(
		(isAboveTreshold) => isAboveTreshold && setIsNavOpen(false),
		breakpoints.lg,
	);

	const [isNavOpen, setIsNavOpen] = useState(false);
	const [searchParams] = useSearchParams();
	const iframeMode = searchParams.get("iframeMode") === "true" ? true : false;

	const roles = useAppSelector(selectRoles);
	const { isDemo } = useEnvironment();

	const togglLeftNavigation = () => setIsNavOpen(!isNavOpen);
	const { tenantHasAccess } = useTenantIsAllowed();

	return (
		<>
			<PageLogger />
			<UserbackProvider
				email={idTokenClaims?.email ?? ""}
				username={username}
				tenantId={tenantId}
				name={name}
				roles={roles?.toString() ?? ""}
				groups={idTokenClaims?.groups?.toString() ?? ""}
				localAccountId
			>
				{isDemo && <DemoSetupDialog />}
				<EnvironmentBanner />
				<TenantNotAllowedDialog />
				{tenantHasAccess && (
					<div className={styles.root}>
						<EntitlementSystem />
						{!iframeMode && (
							<>
								<LeftNavigation
									togglLeftNavigation={setIsNavOpen}
									isNavOpen={isNavOpen}
								/>
							</>
						)}
						<ErrorBoundary>
							<RoutedContent
								navigationNames={NAVIGATION_NAMES}
								department={userData?.department}
								username={username}
							/>
							{/* User drawer should be available on several pages */}
							<UserDrawer />
							<GlobalSnackbar />
						</ErrorBoundary>
						{/* Setup dialogs are not interesting in demo mode */}
						{!isDemo && <SetupDialogs />}
						<Baguette onClick={togglLeftNavigation} navIsOpen={isNavOpen} />
					</div>
				)}
			</UserbackProvider>
		</>
	);
};

export { AuthenticatedContent };
